import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TrendingPanel from "@/components/feature/TrendingPanel";
import { getCatName } from "@/components/feature/ArticleCard";
import {
  articles,
  categories,
  getArticleById,
  getCategoryBySlug,
  getRelatedArticles,
} from "@/mocks/news";
import { formatViews } from "@/utils/format";

const siteUrl = "https://budingmh.com.cn";

export default function DetailPage() {
  const { id = "" } = useParams();
  const article = getArticleById(id);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 每日爆料`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) {
        desc.setAttribute("content", article.summary.slice(0, 120));
      }
    }
  }, [article]);

  if (!article) {
    return (
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-20 text-center">
        <div className="text-5xl text-background-300 mb-4"><i className="ri-error-warning-line"></i></div>
        <h1 className="font-heading text-xl font-bold text-foreground-900">未找到该报道</h1>
        <p className="mt-2 text-foreground-500">内容可能已更新或下架。</p>
        <Link to="/" className="mt-5 inline-flex items-center gap-1 bg-primary-600 text-background-50 px-5 py-2.5 rounded-md hover:bg-primary-500">
          返回首页<i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    );
  }

  const cat = getCategoryBySlug(article.category);
  const related = getRelatedArticles(article, 4);
  const otherCats = categories.filter((c) => c.slug !== article.category);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "NewsArticle",
        headline: article.title,
        description: article.summary,
        image: article.cover,
        author: { "@type": "Organization", name: "每日爆料" },
        publisher: { "@type": "Organization", name: "每日爆料", logo: { "@type": "ImageObject", url: `${siteUrl}/fav.png` } },
        datePublished: "2026-09-09",
        keywords: article.tags.join(","),
        articleSection: cat?.name,
        url: `${siteUrl}/news/${article.id}`,
        mainEntityOfPage: { "@type": "WebPage", "@id": `${siteUrl}/news/${article.id}` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
          { "@type": "ListItem", position: 2, name: `${cat?.name}爆料`, item: `${siteUrl}/category/${article.category}` },
          { "@type": "ListItem", position: 3, name: article.title, item: `${siteUrl}/news/${article.id}` },
        ],
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="bg-gradient-to-b from-primary-50/70 to-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 pt-6 pb-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: `${cat?.name}爆料`, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
            {/* 正文区 */}
            <article className="lg:col-span-2">
              <div className="bg-background-50 border border-background-200 rounded-xl p-6 md:p-9">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="inline-flex items-center gap-1 bg-primary-100 text-primary-800 text-xs px-2.5 py-1 rounded-full font-medium"
                  >
                    {getCatName(article.category)}爆料
                  </Link>
                  {article.breaking && (
                    <span className="inline-flex items-center gap-1 bg-primary-600 text-background-50 text-xs px-2.5 py-1 rounded-full">
                      <i className="ri-flashlight-fill pulse-dot"></i>最新
                    </span>
                  )}
                  {article.hot && (
                    <span className="inline-flex items-center gap-1 bg-accent-100 text-accent-800 text-xs px-2.5 py-1 rounded-full">
                      <i className="ri-fire-line"></i>热议
                    </span>
                  )}
                </div>

                <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug">
                  {article.title}
                </h1>
                <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed border-l-4 border-l-primary-400 pl-4 bg-background-100/60 rounded-r-lg py-3 pr-4">
                  {article.summary}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 pb-5 border-b border-background-100 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs"><i className="ri-user-3-line"></i></span>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>9 月 9 日 · {article.time}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-eye-line"></i>{formatViews(article.views)}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-timer-line"></i>预计阅读 {article.readTime} 分钟</span>
                </div>

                {/* 封面大图 */}
                <figure className="mt-6">
                  <img
                    src={article.cover}
                    alt={article.title}
                    title={`${article.title} - 每日爆料`}
                    className="w-full h-60 md:h-80 object-cover object-top rounded-lg"
                  />
                  <figcaption className="mt-2 text-xs text-foreground-400">
                    每日爆料 · {getCatName(article.category)}栏目现场图
                  </figcaption>
                </figure>

                {/* 正文 */}
                <div className="mt-7 space-y-5">
                  {article.content.map((para, i) => (
                    <p key={i} className="text-[15px] md:text-base text-foreground-800 leading-[1.9]">
                      {para}
                    </p>
                  ))}
                </div>

                {/* 标签 */}
                <div className="mt-7 flex items-center gap-2 flex-wrap border-t border-background-100 pt-5">
                  <span className="text-sm text-foreground-500">本文标签：</span>
                  {article.tags.map((t) => (
                    <span key={t} className="bg-background-100 border border-background-200 text-foreground-600 text-xs px-2.5 py-1 rounded-full">
                      #{t}
                    </span>
                  ))}
                </div>

                {/* 分享/反馈 */}
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-background-100/70 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm text-foreground-600">
                    <span>分享这篇文章：</span>
                    <button type="button" aria-label="分享到微信" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200 hover:border-primary-400 hover:text-primary-700 transition-colors"><i className="ri-wechat-line"></i></button>
                    <button type="button" aria-label="分享到微博" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200 hover:border-primary-400 hover:text-primary-700 transition-colors"><i className="ri-weibo-line"></i></button>
                    <button type="button" aria-label="复制链接" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200 hover:border-primary-400 hover:text-primary-700 transition-colors"><i className="ri-link"></i></button>
                  </div>
                  <Link to="/about#contact" className="text-sm text-primary-700 hover:text-primary-600 inline-flex items-center gap-1">
                    <i className="ri-feedback-line"></i>发现错误？联系更正
                  </Link>
                </div>

                {/* 更多栏目 */}
                <div className="mt-6 flex items-center gap-2 flex-wrap">
                  {otherCats.map((c) => (
                    <Link key={c.slug} to={`/category/${c.slug}`} className="text-xs text-foreground-600 bg-background-100 hover:bg-primary-100 hover:text-primary-700 px-3 py-1.5 rounded-full transition-colors">
                      去 {c.name}栏目 看看 →
                    </Link>
                  ))}
                </div>
              </div>
            </article>

            {/* 侧栏 */}
            <TrendingPanel />
          </div>
        </div>
      </div>

      {/* 相关推荐 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 pt-2 pb-6">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-1.5 h-6 rounded-full bg-accent-500 inline-block"></span>
          <h2 className="font-heading text-xl font-black text-foreground-950">相关推荐 · 你可能还想看</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {related.map((r) => (
            <article key={r.id} className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 hover:shadow-md transition-all">
              <Link to={`/news/${r.id}`} className="block">
                <div className="h-36 md:h-40 overflow-hidden">
                  <img
                    src={r.cover}
                    alt={r.title}
                    title={`${r.title} - 每日爆料`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <span className="text-xs text-primary-700 font-medium">{getCatName(r.category)}</span>
                  <h3 className="mt-1 text-sm font-heading font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                    {r.title}
                  </h3>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}